import {
  FC,
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  useTestFinish,
  useTestQuestionData,
  useTestQuestionUpdate,
} from "@repo/edtech-api";

import {
  DEFAULT_CURRENT_QUESTION,
  LessonStatus,
  TestData,
  TestQuestionData,
  useIdContext,
  useMainContextActions,
  useMainContextValues,
  useTestSolutionActionsContext,
  useTestSolutionValuesContext,
} from "../../../../../../model";
import { CheckTestCover } from "../check-test-cover/CheckTestCover";
import { QuestionByType } from "../question-by-type/QuestionByType";
import { TestSolutionLayout } from "../test-solution-layout/TestSolutionLayout";

type Props = {
  testData?: TestData,
  isSurvey: boolean,
  questions: Array<{
    uid: string,
    title: string,
  }>,
};

export const TestSolution: FC<Props> = ({
  testData,
  isSurvey,
  questions,
}) => {
  const [ isFinishLoading, setIsFinishLoading ] = useState(false);
  const {
    publicationId,
    unitId: mainUnitId,
  } = useIdContext();

  const { currentLesson } = useMainContextValues();
  const { updateLibraryAction } = useMainContextActions();

  const {
    currentQuestionIndex,
    checkComponentCover,
    currentQuestion,
  } = useTestSolutionValuesContext();

  const {
    finishTestExpired,
    finishTestSucceeded,
    questionLoadedFromServer,
    saveLoadingChanged,
    registerSaveAnswerHandler,
    setQuestionIndex,
    toggleCheckCover,
  } = useTestSolutionActionsContext();

  const { unitParentId } = currentLesson ?? {};

  const unitId = unitParentId ?? "";

  const sessionId = testData?.session;
  const currentQuestionId = questions?.[ currentQuestionIndex ]?.uid;

  const {
    data: questionData,
    isLoading: isQuestionsDataLoading,
    mutate: revalidateQuestion,
  } =
    useTestQuestionData({
      publicationId,
      unitId,
      testId: testData?.uid,
      questionId: currentQuestionId,
      session: sessionId,
    });

  const {
    trigger,
    isMutating,
  } = useTestQuestionUpdate({
    publicationId,
    unitId,
    questionId: currentQuestionId,
    session: sessionId,
    questionType: questionData?.questionType,
  });

  const { trigger: finishTest } = useTestFinish({
    publicationId,
    unitId,
    testId: testData?.uid,
    questionId: currentQuestionId,
    session: sessionId,
    mainUnitId,
    timeout: testData?.testTimeout,
    skillLevel: testData?.skillLevel,
    survey: isSurvey,
  });

  useEffect(() => {
    registerSaveAnswerHandler(trigger);

    return () => {
      registerSaveAnswerHandler(null);
    };
  }, [ registerSaveAnswerHandler, trigger ]);

  useEffect(() => {
    saveLoadingChanged(isMutating);
  }, [ isMutating, saveLoadingChanged ]);

  useEffect(() => {
    questionLoadedFromServer(questionData as TestQuestionData | undefined);
  }, [ questionData, questionLoadedFromServer ]);

  const onClickFinishTest = useCallback(async () => {
    if (isFinishLoading) return;

    setIsFinishLoading(true);
    try {
      updateLibraryAction(currentLesson?.nodeId || "", {
        status: LessonStatus.Queued,
      });

      const res = await finishTest();

      if (res?.expired) {
        finishTestExpired();
        return;
      }

      finishTestSucceeded();
    } catch (error) {
      console.error(error);
    } finally {
      setIsFinishLoading(false);
    }
  }, [
    currentLesson?.nodeId,
    finishTest,
    finishTestExpired,
    finishTestSucceeded,
    isFinishLoading,
    updateLibraryAction,
  ]);

  const onClickCheckTest = useCallback(() => {
    setQuestionIndex(0);
    toggleCheckCover(false);
    revalidateQuestion();
  }, [
    setQuestionIndex,
    toggleCheckCover,
    revalidateQuestion,
  ]);

  if (checkComponentCover) {
    return (
      <CheckTestCover
        isFinishLoading={isFinishLoading}
        isSurvey={isSurvey}
        onClickCheckTest={onClickCheckTest}
        onClickFinishTest={onClickFinishTest}
      />
    );
  }

  return (
    <TestSolutionLayout
      isQuestionsLoading={isQuestionsDataLoading}
      isSurvey={isSurvey}
      questionData={currentQuestion || DEFAULT_CURRENT_QUESTION}
      questionsCount={questions.length}
    >
      <>{!isQuestionsDataLoading && questionData && <QuestionByType />}</>
    </TestSolutionLayout>
  );
};
